import loadable from '@loadable/component';
import ErrorBoundary from 'components/ErrorBoundary';
import React, {
  ComponentType,
  PureComponent,
  ReactElement,
  ReactNode,
} from 'react';
import { Route, RouteComponentProps, RouteProps } from 'react-router-dom';
import { isCordova } from 'utils/platform';

import { WlWindow } from '../../../../typings/globals/cordova';

const LoadableNavbar = loadable(
  () =>
    import(
      /* webpackChunkName: "navbar" */ '~/reusable-components/navigation/Navbar'
    ),
  {},
  true,
);
const LoadableTabBar = loadable(
  () =>
    import(
      /* webpackChunkName: "tabbar" */ '~/reusable-components/navigation/TabBar'
    ),
  {},
  true,
);
const LoadableFooter = loadable(
  () => import(/* webpackChunkName: "footer" */ 'modules/common/Footer'),
  {},
  true,
);

declare const window: WlWindow;

export type NavigationableLayoutProps = RouteProps & {
  exact?: boolean;
  overlay?: ReactNode;
  transparentNavbar?: boolean;
  nonFixedNavbar?: boolean;
  component: ComponentType;
  onTopOf?: ComponentType;
  showSearchInNavbar?: boolean;
  showFullSearchInNavbar?: boolean;
  hideFooter?: boolean;
  showBottomNavigation?: boolean;
  subNavbar?: ReactElement;
  subNavbarMobile?: ReactElement;
  bottomBar?: ReactElement;
  noNavbar?: boolean;
  showZendesk?: boolean;
};

class NavigationableLayout extends PureComponent<NavigationableLayoutProps> {
  previousContent = null;

  componentDidMount() {
    this.toggleZendesk();
  }

  componentWillReceiveProps(nextProps) {
    // Keep content rendered in the background when rendering an overlay
    if (!nextProps.children && this.props.children) {
      this.previousContent = this.props.children;
    }
  }

  componentDidUpdate() {
    this.toggleZendesk();
  }

  toggleZendesk() {
    const showZendesk =
      this.props.showZendesk === true || this.props.showZendesk === undefined;

    if (showZendesk && window.innerWidth >= 768) {
      if (typeof window.zE === 'function') {
        window.zE('messenger', 'show');
      }
    } else if (!showZendesk) {
      if (typeof window.zE === 'function') {
        window.zE('messenger', 'hide');
      }
    }
  }

  renderContent = (matchProps: RouteComponentProps<any>) => {
    const {
      component,
      onTopOf,
      overlay,
      showSearchInNavbar = true,
      showFullSearchInNavbar = false,
      hideFooter = false,
      noNavbar,
      transparentNavbar = false,
      nonFixedNavbar,
      showBottomNavigation,
      subNavbar,
      subNavbarMobile,
    } = this.props;
    const SubComponent = component;
    const UnderlyingPage = onTopOf ?? null;
    return (
      <>
        {!noNavbar && (
          <LoadableNavbar
            fixed={nonFixedNavbar ? 'none' : 'all'}
            variant={transparentNavbar ? 'transparent' : 'default'}
            showSearch={showSearchInNavbar}
            showFullSearch={showFullSearchInNavbar}
            subNavbar={subNavbar}
            subNavbarMobile={subNavbarMobile}
          />
        )}
        <ErrorBoundary>
          {onTopOf && <UnderlyingPage />}
          <SubComponent {...matchProps} />
        </ErrorBoundary>
        {overlay}
        {hideFooter ? null : <LoadableFooter />}
        {showBottomNavigation && isCordova() && (
          <LoadableTabBar bottomBar={this.props.bottomBar} />
        )}
      </>
    );
  };

  render() {
    const { path, location, exact } = this.props;

    return (
      <Route
        exact={exact}
        location={location}
        path={path}
        render={this.renderContent}
      />
    );
  }
}

export default NavigationableLayout;
